import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  Database,
  BarChart3,
  Plus,
  X,
  RefreshCw,
  Check,
  XCircle,
} from "lucide-react";
import { apiClient } from "../services/api";

// ─── helpers ────────────────────────────────────────────────────────────────

const ScoreRing = ({ score }) => {
  const color = score >= 85 ? "#22c55e" : score >= 60 ? "#3b82f6" : score >= 30 ? "#f59e0b" : "#ef4444";
  const r = 40, circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="100" height="100" className="-rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/30" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute text-center">
        <span className="text-2xl font-bold">{score}</span>
        <span className="block text-xs text-muted-foreground">/100</span>
      </div>
    </div>
  );
};

const KpiCard = ({ title, value, sub, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
    red: "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400",
    orange: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
    green: "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    purple: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}><Icon size={20} /></div>
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  in_review: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};
// ─── Tab: Dashboard ──────────────────────────────────────────────────────────

const DashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/gdpr/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Compliance score */}
      <div className="bg-card border border-border rounded-xl p-6 flex items-center gap-8">
        <ScoreRing score={data.compliance_score} />
        <div>
          <h3 className="text-xl font-bold">{data.compliance_level}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("gdprHub.dashboard.scoreTitle")}</p>
          <div className="mt-3 space-y-1 text-sm">
            <p className={data.consent_coverage_pct >= 80 ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}>
              {data.consent_coverage_pct >= 80 ? "✓" : "⚠"} {t("gdprHub.dashboard.consentCoverage", { pct: data.consent_coverage_pct })}
            </p>
            <p className={data.overdue_dsar === 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
              {data.overdue_dsar === 0 ? "✓" : "✗"} {t("gdprHub.dashboard.overdueRequests", { count: data.overdue_dsar })}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title={t("gdprHub.dashboard.kpiEmployees")} value={data.total_employees} icon={Shield} color="blue" />
        <KpiCard title={t("gdprHub.dashboard.kpiConsents")} value={`${data.consent_coverage_pct}%`} sub={t("gdprHub.dashboard.kpiConsentsSub", { count: data.employees_with_consent })} icon={CheckCircle} color="green" />
        <KpiCard title={t("gdprHub.dashboard.kpiDsar")} value={data.total_dsar} sub={t("gdprHub.dashboard.kpiDsarSub", { count: data.pending_dsar })} icon={FileText} color="purple" />
        <KpiCard title={t("gdprHub.dashboard.kpiOverdue")} value={data.overdue_dsar} sub={t("gdprHub.dashboard.kpiOverdueSub")} icon={AlertTriangle} color={data.overdue_dsar > 0 ? "red" : "green"} />
      </div>
    </div>
  );
};

// ─── Tab: Consentements ───────────────────────────────────────────────────────

const RecordConsentModal = ({ onClose, onSaved }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", purpose: "payroll", is_granted: true, legal_basis: "consent", collection_method: "form" });
  const [saving, setSaving] = useState(false);

  const PURPOSES = ["payroll", "health_data", "biometrics", "marketing", "analytics", "third_party_transfer"];
  const LEGAL_BASES = [
    { value: "consent", label: t("gdprHub.consents.legalBasisConsent") },
    { value: "contract", label: t("gdprHub.consents.legalBasisContract") },
    { value: "legal_obligation", label: t("gdprHub.consents.legalBasisObligation") },
    { value: "legitimate_interest", label: t("gdprHub.consents.legalBasisInterest") },
  ];

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data.items || []));
  }, []);

  const save = async () => {
    if (!form.employee_id) return;
    setSaving(true);
    try {
      await apiClient.post("v1/gdpr/consents", { ...form, employee_id: parseInt(form.employee_id) });
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("gdprHub.consents.modalTitle")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("gdprHub.consents.employee")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
              <option value="">{t("gdprHub.consents.selectPlaceholder")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{t("gdprHub.consents.purpose")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("gdprHub.consents.legalBasis")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.legal_basis} onChange={(e) => setForm({ ...form, legal_basis: e.target.value })}>
                {LEGAL_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t("gdprHub.consents.statusLabel")}</label>
            <div className="flex gap-3">
              <button onClick={() => setForm({ ...form, is_granted: true })}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${form.is_granted ? "border-green-500 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400" : "border-border"}`}>
                <Check size={14} className="inline mr-1" /> {t("gdprHub.consents.granted")}
              </button>
              <button onClick={() => setForm({ ...form, is_granted: false })}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${!form.is_granted ? "border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400" : "border-border"}`}>
                <XCircle size={14} className="inline mr-1" /> {t("gdprHub.consents.withdrawn")}
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.employee_id}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("gdprHub.consents.saving") : t("gdprHub.consents.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConsentsTab = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("v1/gdpr/consents").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("gdprHub.consents.count", { count: items.length })}</p>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-muted-foreground hover:text-foreground"><RefreshCw size={14} /></button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
            <Plus size={15} /> {t("gdprHub.consents.record")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.consents.colEmployee")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.consents.colPurpose")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.consents.colLegalBasis")}</th>
                <th className="text-center px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.consents.colStatus")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.consents.colDate")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-muted/20">
                  <td className="px-5 py-3 font-medium">{c.employee_name}</td>
                  <td className="px-5 py-3 text-muted-foreground">{c.purpose}</td>
                  <td className="px-5 py-3 text-muted-foreground">{c.legal_basis}</td>
                  <td className="px-5 py-3 text-center">
                    {c.is_granted ? (
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium"><CheckCircle size={13} /> {t("gdprHub.consents.granted")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-medium"><XCircle size={13} /> {t("gdprHub.consents.withdrawn")}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">{c.granted_at ? new Date(c.granted_at).toLocaleDateString() : c.withdrawn_at ? new Date(c.withdrawn_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">{t("gdprHub.consents.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <RecordConsentModal onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
};

// ─── Tab: DSAR ────────────────────────────────────────────────────────────────

const CreateDSARModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", request_type: "access", description: "" });
  const [saving, setSaving] = useState(false);

  const TYPES = [
    { value: "access", label: t("gdprHub.dsar.typeAccess") },
    { value: "erasure", label: t("gdprHub.dsar.typeErasure") },
    { value: "portability", label: t("gdprHub.dsar.typePortability") },
    { value: "rectification", label: t("gdprHub.dsar.typeRectification") },
    { value: "restriction", label: t("gdprHub.dsar.typeRestriction") },
    { value: "objection", label: t("gdprHub.dsar.typeObjection") },
  ];

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data.items || []));
  }, []);

  const save = async () => {
    if (!form.employee_id) return;
    setSaving(true);
    try {
      await apiClient.post("v1/gdpr/dsar", { ...form, employee_id: parseInt(form.employee_id) });
      onCreated();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("gdprHub.dsar.modalTitle")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("gdprHub.dsar.employee")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
              <option value="">{t("gdprHub.dsar.selectPlaceholder")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("gdprHub.dsar.requestType")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })}>
              {TYPES.map((ty) => <option key={ty.value} value={ty.value}>{ty.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("gdprHub.dsar.description")}</label>
            <textarea className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" rows={3}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.employee_id}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("gdprHub.dsar.creating") : t("gdprHub.dsar.create")}
          </button>
        </div>
      </div>
    </div>
  );
};

const DSARTab = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("v1/gdpr/dsar").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await apiClient.patch(`v1/gdpr/dsar/${id}`, { status });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("gdprHub.dsar.count", { count: items.length })}</p>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-muted-foreground hover:text-foreground"><RefreshCw size={14} /></button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
            <Plus size={15} /> {t("gdprHub.dsar.new")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((req) => (
            <div key={req.id} className={`bg-card border rounded-xl p-4 ${req.is_overdue ? "border-red-300 dark:border-red-700" : "border-border"}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{req.employee_name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[req.status]}`}>{t(`gdprHub.status.${req.status}`, { defaultValue: req.status })}</span>
                    {req.is_overdue && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{t("gdprHub.dsar.overdueLabel")}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{t("gdprHub.dsar.submittedLine", { type: req.request_type_label, date: new Date(req.submitted_at).toLocaleDateString(), due: req.due_date || "—" })}</p>
                  {req.description && <p className="text-sm mt-1 text-muted-foreground italic">{req.description}</p>}
                </div>
                {req.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => updateStatus(req.id, "in_review")} className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 hover:opacity-80">{t("gdprHub.dsar.actionInReview")}</button>
                    <button onClick={() => updateStatus(req.id, "completed")} className="px-3 py-1.5 text-xs rounded-lg bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 hover:opacity-80">{t("gdprHub.dsar.actionCompleted")}</button>
                    <button onClick={() => updateStatus(req.id, "rejected")} className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 hover:opacity-80">{t("gdprHub.dsar.actionReject")}</button>
                  </div>
                )}
                {req.status === "in_review" && (
                  <button onClick={() => updateStatus(req.id, "completed")} className="px-3 py-1.5 text-xs rounded-lg bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 hover:opacity-80">{t("gdprHub.dsar.markCompleted")}</button>
                )}
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-center py-12 bg-card border border-border rounded-xl text-muted-foreground">
              <FileText size={36} className="mx-auto mb-2 opacity-50" />
              {t("gdprHub.dsar.empty")}
            </div>
          )}
        </div>
      )}

      {showModal && <CreateDSARModal onClose={() => setShowModal(false)} onCreated={load} />}
    </div>
  );
};

// ─── Tab: Rétention ───────────────────────────────────────────────────────────

const RetentionTab = () => {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/gdpr/retention-policies").then((r) => setPolicies(r.data)).finally(() => setLoading(false));
  }, []);

  const ACTION_LABELS = { anonymize: t("gdprHub.retention.actionAnonymize"), delete: t("gdprHub.retention.actionDelete"), archive: t("gdprHub.retention.actionArchive") };
  const ACTION_COLORS = {
    anonymize: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    delete: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    archive: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("gdprHub.retention.subtitle")}</p>
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.retention.colCategory")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.retention.colRetention")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.retention.colAction")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("gdprHub.retention.colLegalRef")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {policies.map((p) => (
                <tr key={p.id} className="hover:bg-muted/20">
                  <td className="px-5 py-3">
                    <p className="font-medium">{p.data_category}</p>
                    {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                  </td>
                  <td className="px-5 py-3 font-medium">{p.retention_label}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[p.action_on_expiry] || ""}`}>
                      {ACTION_LABELS[p.action_on_expiry] || p.action_on_expiry}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{p.legal_reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Hub ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "consents", icon: CheckCircle },
  { id: "dsar", icon: FileText },
  { id: "retention", icon: Database },
];

export default function GDPRHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "consents": return <ConsentsTab />;
      case "dsar": return <DSARTab />;
      case "retention": return <RetentionTab />;
      default: return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield size={24} className="text-primary" />
            {t("gdprHub.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("gdprHub.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit flex-wrap">
        {TABS.map(({ id, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon size={15} />
            {t(`gdprHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      {renderContent()}
    </div>
  );
}
